
import { Dialog, DialogContent } from "@/components/ui/dialog";
import type { LogicRule } from "@/lib/game-utils";
import type { Word } from "@/lib/game-utils";

interface ReasoningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: LogicRule | null;
  words?: Word[];
}

export function ReasoningModal({ open, onOpenChange, rule, words = [] }: ReasoningModalProps) {
  if (!rule) return null;
  
  // Function to highlight game words in the reasoning text
  const highlightGameWords = (text: string, gameWords: Word[]): React.ReactNode => {
    if (!text || !gameWords.length) return text;
    
    // Split the text by spaces and other punctuation to properly identify words
    const parts = text.split(/(\s+|[.,;:!?()])/g);
    
    return parts.map((part, index) => {
      // Check if this part is one of our game words (case insensitive)
      const matchingWord = gameWords.find(
        word => part.toLowerCase() === String(word).toLowerCase()
      );
      
      return matchingWord ? (
        <strong key={index}>{part}</strong>
      ) : (
        <span key={index}>{part}</span>
      );
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-0">from today's</p>
            <h2 className="text-xl font-bold">Logic Setter</h2>
            
            {rule.spectrum && rule.spectrum[0] && rule.spectrum[1] && (
              <p className="text-sm text-gray-600 mt-4">
                {rule.name}: <span>{rule.spectrum[0]}</span>
                <span className="mx-1"><i>to</i></span>
                <span>{rule.spectrum[1]}</span>
              </p>
            )}
          </div>
          
          {rule.reasoning ? (
            <div className="prose prose-sm">
              <p className="whitespace-pre-wrap">
                {highlightGameWords(rule.reasoning, words)}
              </p>
            </div>
          ) : (
            <p className="text-gray-500 text-center italic">No additional reasoning available for this logic.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
